import type {
  BrowserNotificationRuntime,
  NotificationPermissionState,
} from './browser-notification-runtime'
import type { WebPushServerState } from './web-push-state'
import { serializePushSubscription } from './web-push-client'

export interface WebPushReconciliationClient {
  refresh: (input: {
    expectedUserId: string
    expectedSessionId: string
    installationId: string
    bindingToken: string
    endpoint: string
    expirationTime: number | null
    keys: { p256dh: string, auth: string }
  }) => Promise<unknown>
  deactivate: (input: {
    expectedUserId: string
    expectedSessionId: string
    installationId: string
    bindingToken: string
    reason: 'permission_revoked' | 'subscription_missing'
  }) => Promise<unknown>
}

export interface WebPushReconciliationInput {
  expectedUserId: string | null
  expectedSessionId: string | null
  installationId: string | null
  bindingToken: string | null
  permission: NotificationPermissionState
  serverState?: WebPushServerState
  serviceAvailable?: boolean
  runtime: BrowserNotificationRuntime
  client: WebPushReconciliationClient
  clearInstallationId: () => void
  adoptSubscription: (
    subscription: PushSubscription,
  ) => boolean | Promise<boolean>
  invalidateSubscriptionAdoption: () => boolean
  isCurrent?: () => boolean
}

async function removeLocalPush(
  runtime: BrowserNotificationRuntime,
  isCurrent: () => boolean,
): Promise<void> {
  if (!isCurrent())
    return
  await runtime.unsubscribeFromPush()
  if (!isCurrent())
    return
  try {
    await runtime.closeWebPushNotifications()
  }
  catch {}
}

export async function reconcileWebPushIdentity({
  expectedUserId,
  expectedSessionId,
  installationId,
  bindingToken,
  permission,
  serverState,
  serviceAvailable,
  runtime,
  client,
  clearInstallationId,
  adoptSubscription,
  invalidateSubscriptionAdoption,
  isCurrent = () => true,
}: WebPushReconciliationInput): Promise<void> {
  let subscription: PushSubscription | null
  try {
    subscription = await runtime.getPushSubscription()
  }
  catch {
    return
  }
  if (!isCurrent())
    return

  if (
    !expectedUserId
    || !expectedSessionId
    || !installationId
    || !bindingToken
  ) {
    await removeLocalPush(runtime, isCurrent)
    if (!isCurrent())
      return
    clearInstallationId()
    return
  }

  if (!serverState)
    return

  if (
    serverState === 'unregistered'
    || serverState === 'locally-disabled'
    || serverState === 'server-invalidated'
  ) {
    if (
      serverState !== 'unregistered'
      && !invalidateSubscriptionAdoption()
    ) {
      throw new Error('Push subscription adoption changed')
    }
    await removeLocalPush(runtime, isCurrent)
    if (!isCurrent())
      return
    if (serverState === 'unregistered')
      clearInstallationId()
    return
  }

  if (permission !== 'granted') {
    if (!isCurrent())
      return
    await client.deactivate({
      expectedUserId,
      expectedSessionId,
      installationId,
      bindingToken,
      reason: 'permission_revoked',
    })
    if (!isCurrent())
      return
    if (!invalidateSubscriptionAdoption())
      throw new Error('Push subscription adoption changed')
    await removeLocalPush(runtime, isCurrent)
    return
  }

  if (!subscription) {
    if (!isCurrent())
      return
    await client.deactivate({
      expectedUserId,
      expectedSessionId,
      installationId,
      bindingToken,
      reason: 'subscription_missing',
    })
    if (!isCurrent())
      return
    if (!invalidateSubscriptionAdoption())
      throw new Error('Push subscription adoption changed')
    try {
      await runtime.closeWebPushNotifications()
    }
    catch {}
    return
  }

  if (serviceAvailable) {
    if (!isCurrent())
      return
    await client.refresh({
      expectedUserId,
      expectedSessionId,
      installationId,
      bindingToken,
      ...serializePushSubscription(subscription),
    })
    if (!isCurrent())
      return
    if (!(await adoptSubscription(subscription)))
      throw new Error('Push subscription adoption changed')
  }
}
